import React, { useState } from 'react';
import { RecordingSession } from '@/types';
import { VideoPlayer } from '@/components/VideoPlayer';
import { formatDuration, formatFileSize, formatDate } from '@/utils/helpers';
import {
  Film,
  Users,
  Calendar,
  Clock,
  HardDrive,
  FolderOpen,
  Share2,
  Trash2,
  RefreshCw,
  Crop,
  CheckCircle2,
  ArrowLeft,
  AlertTriangle,
  Download,
  Sparkles
} from 'lucide-react';

interface RecordingDetailsScreenProps {
  session: RecordingSession | null;
  onBack: () => void;
  onOpenReview: () => void;
  onOpenExport: () => void;
  onReprocess: () => void;
  onDelete: (id: string) => void;
}

export const RecordingDetailsScreen: React.FC<RecordingDetailsScreenProps> = ({
  session,
  onBack,
  onOpenReview,
  onOpenExport,
  onReprocess,
  onDelete
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (!session) {
    return (
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center text-center space-y-4">
        <Film className="w-12 h-12 text-brand-muted" />
        <h2 className="text-xl font-bold text-brand-text">No Recording Selected</h2>
        <p className="text-xs text-brand-muted">Please select a recording from the library.</p>
        <button
          onClick={onBack}
          className="px-4 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-semibold hover:bg-brand-elevated"
        >
          Back to Library
        </button>
      </div>
    );
  }

  const handleRevealInFolder = () => {
    if (window.electronAPI && session.filePath) {
      window.electronAPI.showItemInFolder(session.filePath);
    } else {
      alert(`File path: ${session.filePath}`);
    }
  };

  const handleDownloadRaw = async () => {
    const safeTitle = session.title.replace(/[^a-zA-Z0-9_-]/g, '_');
    const filename = `${safeTitle}.mp4`;
    if (session.blobUrl) {
      const a = document.createElement('a');
      a.href = session.blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    if (window.electronAPI && session.filePath) {
      try {
        const buf = await window.electronAPI.readRecordingFile(session.filePath);
        const blob = new Blob([buf], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        return;
      } catch {
        window.electronAPI.showItemInFolder(session.filePath);
      }
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
      {/* Top Bar Navigation */}
      <div className="flex items-center justify-between pb-4 border-b border-brand-border/60">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-xs font-semibold text-brand-muted hover:text-brand-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Library</span>
        </button>

        <div className="flex items-center gap-2">
          {!session.isSingleRecording && !session.isGroupMeeting && (
            <button
              onClick={onReprocess}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-xs text-brand-muted hover:text-brand-text transition-colors"
              title="Re-run computer vision tile detection"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Re-detect Tiles</span>
            </button>
          )}

          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-xs text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Video Player and Playback (7 Columns) */}
        <div className="lg:col-span-7 space-y-4">
          <VideoPlayer
            src={session.filePath}
            blobUrl={session.blobUrl}
            poster={session.thumbnailUrl}
          />

          <div>
            <h1 className="text-xl font-bold text-brand-text">{session.title}</h1>
            <p className="text-xs text-brand-muted mt-1 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(session.createdAt)}</span>
            </p>
          </div>
        </div>

        {/* RIGHT: Metadata & Participant List (5 Columns) */}
        <div className="lg:col-span-5 space-y-5">
          {/* Action CTAs */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-3">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider">
              Project Actions
            </h3>

            {session.isSingleRecording || session.isGroupMeeting ? (
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 text-xs text-purple-300 space-y-1">
                  <span className="font-bold flex items-center gap-1.5 text-purple-200">
                    <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                    Single Master Recording
                  </span>
                  <p className="text-[11px] text-brand-muted leading-relaxed">
                    This group meeting is stored as one unified master video of all participants. Individual speaker separation is disabled for group meetings.
                  </p>
                </div>

                <button
                  onClick={handleDownloadRaw}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 text-white text-xs font-semibold shadow-glow transition-all active:scale-95 cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Complete Meeting Video</span>
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={onOpenReview}
                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-xs font-semibold text-brand-text transition-colors"
                  >
                    <Crop className="w-4 h-4 text-brand-secondary" />
                    <span>Adjust Crops</span>
                  </button>

                  <button
                    onClick={onOpenExport}
                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-glow transition-all active:scale-95"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Export Clips</span>
                  </button>
                </div>

                <button
                  onClick={handleDownloadRaw}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-md transition-all active:scale-95 cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Raw MP4 Video</span>
                </button>
              </>
            )}
          </div>

          {/* Technical Metadata Card */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-3">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider">
              Technical Metadata
            </h3>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-2.5 rounded-xl bg-brand-elevated/50 border border-brand-border/40">
                <span className="text-[10px] text-brand-muted flex items-center gap-1">
                  <Clock className="w-3 h-3 text-brand-secondary" />
                  Duration
                </span>
                <span className="font-mono font-semibold text-brand-text mt-0.5 block">
                  {formatDuration(session.durationSec)}
                </span>
              </div>

              <div className="p-2.5 rounded-xl bg-brand-elevated/50 border border-brand-border/40">
                <span className="text-[10px] text-brand-muted flex items-center gap-1">
                  <Film className="w-3 h-3 text-brand-accent" />
                  Resolution
                </span>
                <span className="font-mono font-semibold text-brand-text mt-0.5 block">
                  {session.resolution.width}x{session.resolution.height} @ {session.fps}fps
                </span>
              </div>

              <div className="p-2.5 rounded-xl bg-brand-elevated/50 border border-brand-border/40">
                <span className="text-[10px] text-brand-muted flex items-center gap-1">
                  <HardDrive className="w-3 h-3 text-emerald-400" />
                  File Size
                </span>
                <span className="font-mono font-semibold text-brand-text mt-0.5 block">
                  {formatFileSize(session.fileSize)}
                </span>
              </div>

              <div className="p-2.5 rounded-xl bg-brand-elevated/50 border border-brand-border/40">
                <span className="text-[10px] text-brand-muted flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-brand-secondary" />
                  Layout
                </span>
                <span className="font-semibold text-brand-text mt-0.5 block capitalize">
                  {session.detectedLayout?.replace('_', ' ') || 'Auto Grid'}
                </span>
              </div>
            </div>

            {/* Local File Location */}
            <div className="pt-2 border-t border-brand-border/60">
              <div className="flex items-center justify-between text-xs">
                <span className="text-brand-muted truncate max-w-[180px] font-mono text-[11px]" title={session.filePath}>
                  {session.filePath || 'Browser Cache (IndexedDB)'}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleDownloadRaw}
                    className="flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 font-semibold"
                    title="Download Raw MP4 File"
                  >
                    <Download className="w-3 h-3" />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={handleRevealInFolder}
                    className="flex items-center gap-1 text-[11px] text-brand-accent hover:text-brand-accent-hover font-medium shrink-0"
                  >
                    <FolderOpen className="w-3 h-3" />
                    <span>Reveal</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Detected Participants Grid (or Group Meeting Single Format Card) */}
          {session.isSingleRecording || session.isGroupMeeting ? (
            <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-3">
              <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-brand-accent" />
                Single Group Video Format
              </h3>

              <div className="p-3 rounded-xl bg-brand-elevated/40 border border-brand-border/40 text-xs text-brand-muted space-y-2">
                <div className="flex items-center gap-2 text-brand-text font-semibold">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>All Speakers Unified in One Video</span>
                </div>
                <p className="text-[11px] leading-relaxed">
                  As requested for group meetings, all participant video tiles, shared screens, and audio feeds are recorded together into this single master recording file. Separate participant splitting is not applied to group meetings.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-brand-secondary" />
                  Detected Participants ({session.participants.length})
                </h3>
                <button
                  onClick={onOpenReview}
                  className="text-[11px] text-brand-accent hover:underline font-medium"
                >
                  View Separate Videos
                </button>
              </div>

              <div className="space-y-2">
                {session.participants.map((p) => (
                  <div
                    key={p.id}
                    className="p-2.5 rounded-xl bg-brand-elevated/40 border border-brand-border/40 flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 aspect-video rounded bg-black/60 overflow-hidden shrink-0 flex items-center justify-center">
                        {p.thumbnailUrl ? (
                          <img src={p.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-3.5 h-3.5 text-brand-muted" />
                        )}
                      </div>
                      <span className="font-medium text-brand-text">{p.name}</span>
                    </div>

                    <span className="text-[10px] font-mono text-brand-secondary">
                      {p.confidence}% confidence
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
          <div className="max-w-md w-full bg-brand-surface border border-brand-border rounded-3xl p-6 shadow-elevated space-y-4">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="font-bold text-base text-brand-text">Delete Recording Project?</h3>
            </div>

            <p className="text-xs text-brand-muted leading-relaxed">
              Are you sure you want to permanently delete "{session.title}"? This will remove the recording metadata and associated participant tracks.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-medium text-brand-muted hover:text-brand-text bg-brand-elevated border border-brand-border transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  onDelete(session.id);
                  onBack();
                }}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 shadow-md transition-all"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
